import * as Tabs from '@radix-ui/react-tabs';
import { ArrowRight } from 'lucide-react';

import { SecondaryCTA } from '../components/SecondaryCTA';
import { SectionWrapper } from '../components/SectionWrapper';

import styles from './AudienceTabsSection.module.css';

const audiences = [
  {
    body: [
      'Kudu lets academic leadership reduce student costs without asking faculty to give up course ownership or pedagogical judgment.',
      'The platform keeps course quality, accessibility, and AI policy in one reviewable place instead of scattering responsibility across departments.',
    ],
    cta: 'See the institutional case',
    href: '/contact',
    label: 'Provost / VP TnL',
    title: 'Academic quality and affordability can move together.',
    value: 'provost',
  },
  {
    body: [
      'Departments can standardize a strong default course while still leaving room for individual faculty to edit sections, questions, and schedules.',
      'That means fewer orphaned pilots and less reinvention every semester.',
    ],
    cta: 'Plan a department pilot',
    href: '/contact',
    label: 'Dept Chair',
    title: 'A shared course foundation faculty can actually use.',
    value: 'chair',
  },
  {
    body: [
      'One vendor review covers the textbook, homework, polling, AI, accessibility, and data flow instead of five separate products.',
      'Kudu gives IT a defensible answer on FERPA, accessibility, AI guardrails, and vendor risk.',
    ],
    cta: 'Ask for IT documentation',
    href: '/contact',
    label: 'CIO / IT',
    title: 'One review. One integration path. AI you can explain.',
    value: 'cio',
  },
  {
    body: [
      'At $25 per student per semester, Kudu changes the arithmetic of inclusive access from damage control to a sustainable program.',
      'The all-in price replaces the expensive bundle, not just one line item.',
    ],
    cta: 'Review pricing',
    // The pricing section is on this page.
    href: '#pricing',
    label: 'CFO',
    title: 'The economics finally pencil out.',
    value: 'cfo',
  },
  {
    body: [
      'Kudu is built to fit inclusive-access workflows with a clean student price and one product that replaces the publisher stack.',
      'Bookstores can support affordability goals without becoming the help desk for five disconnected tools.',
    ],
    cta: 'Bookstore conversation',
    href: '/contact',
    label: 'Bookstore',
    title: 'A simpler inclusive-access offer for students.',
    value: 'bookstore',
  },
  {
    body: [
      'OpenStax content is not the hard part. Students need accessible delivery, homework, analytics, and support around the OER.',
      'Kudu provides the infrastructure layer that makes OER practical at course scale.',
    ],
    cta: 'Explore OER support',
    href: '/contact',
    label: 'Librarian',
    title: 'OER quality plus the infrastructure students need.',
    value: 'librarian',
  },
] as const;

export function AudienceTabsSection() {
  return (
    <SectionWrapper background="white" data-testid="institutions-section-audience-tabs">
      <div className={styles.section}>
        <div className={styles.header}>
          <p className={styles.eyebrow}>YOUR TEAM</p>
          <h2 className={styles.title}>Different roles. Different concerns. Same partner.</h2>
          <p className={styles.subhead}>
            Pick the role closest to yours to see how Kudu addresses your
            specific priorities and constraints.
          </p>
        </div>

        <Tabs.Root className={styles.tabs} defaultValue={audiences[0].value}>
          <Tabs.List aria-label="Institution audience roles" className={styles.tabList}>
            {audiences.map((audience) => (
              <Tabs.Trigger
                className={styles.tabTrigger}
                key={audience.value}
                value={audience.value}
              >
                {audience.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {audiences.map((audience) => (
            <Tabs.Content
              className={styles.panel}
              key={audience.value}
              value={audience.value}
            >
              <div className={styles.panelCopy}>
                <h3>{audience.title}</h3>
                {audience.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <SecondaryCTA
                className={styles.panelCta}
                href={audience.href}
                icon={<ArrowRight aria-hidden="true" size={16} />}
                label={audience.cta}
              />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </SectionWrapper>
  );
}
